import { Flex, Tabs } from '@mantine/core'
import { useMemo } from 'react'

import { useQueryGetAllBookingDocuments } from '@flecto/core/saas'
import { useQueryGetUserLogged } from '@flecto/core/shared'
import { useTranslation } from '@flecto/translations'
import { type CoreBooking, type CoreBookingStatus } from '@flecto/types'
import { Badge } from '@flecto/ui'

import {
  CardBookingCustomerSelection,
  CardBookingDatesSelection,
  CardBookingInsuranceStatus,
  CardBookingProductsSelection,
  CardBookingNotes,
  CardBookingServicesSelection,
  DocumentBox,
  InsuranceMobileTab,
  OverviewCardNext,
} from '../../..'
import PaymentRental from '../../../shared-ui-modules/@legacy/src/_saas/bookings/PaymentRental'

type BookingTabsProps = {
  booking: CoreBooking
}

export function BookingTabs({ booking }: BookingTabsProps) {
  const { t: tt } = useTranslation('saas', { keyPrefix: 'create-booking' })
  const { data: user } = useQueryGetUserLogged()
  const { data: documents } = useQueryGetAllBookingDocuments({
    bookingId: booking?.internal_id,
  })

  const files = documents?.booking_document || []

  const countFiles = useMemo(() => {
    return files.length
  }, [files])

  const tabs = [
    {
      id: 'general',
      title: tt('tabs.general'),
    },
    {
      id: 'documents',
      title: (
        <>
          {tt('tabs.documents')}
          {countFiles > 0 && (
            <Badge
              ml='sm'
              size='sm'
              color='gray'
              style={{
                fontWeight: 500,
              }}
            >
              {countFiles}
            </Badge>
          )}
        </>
      ),
      component: (
        <DocumentBox bookingStatus={booking?.status as CoreBookingStatus} />
      ),
    },
    {
      id: 'totals',
      title: tt('tabs.totals'),
      component: (
        <>
          <OverviewCardNext />
          <CardBookingInsuranceStatus bookingId={booking?.internal_id} />
          <PaymentRental />
        </>
      ),
    },
    {
      id: 'insurances',
      title: tt('tabs.insurances'),
      component: user?.meta.insured && (
        <InsuranceMobileTab bookingId={booking?.internal_id} />
      ),
    },
  ]

  return (
    <Tabs defaultValue='general'>
      <Tabs.List>
        {tabs.map((tab) => (
          <Tabs.Tab key={tab.id} value={tab.id} data-testid={`tab-${tab.id}`}>
            {tab.title}
          </Tabs.Tab>
        ))}
      </Tabs.List>

      {tabs.map((tab, index) => (
        <Tabs.Panel pt='lg' value={tab.id} key={tab.id}>
          {index === 0 ? (
            <Flex direction='column' gap='lg'>
              <CardBookingCustomerSelection bookingId={booking?.internal_id} />
              <CardBookingDatesSelection bookingId={booking?.internal_id} />
              <CardBookingProductsSelection bookingId={booking?.internal_id} />
              <CardBookingServicesSelection bookingId={booking?.internal_id} />
              <CardBookingNotes bookingId={Number(booking?.internal_id)} />
            </Flex>
          ) : (
            <Flex direction='column' gap='lg'>
              {tab.component}
            </Flex>
          )}
        </Tabs.Panel>
      ))}
    </Tabs>
  )
}
